import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Info, History, Crown, Lock, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import VideoModal from "../components/VideoModal";
import StickerReward from "../components/StickerReward";
import SubscriptionPlans from "../components/SubscriptionPlans";
import UpgradeSheet from "../components/UpgradeSheet";

const AGE_FILTERS = ["All", "3-5", "6-8", "9-12", "Parent"];
const CATEGORIES = ["Cartoons", "Science", "Math Games", "Bedtime Stories", "Parent Ed"];

function Row({ title, videos, onPlay, testid, isKid, watchSet, onBookmark }) {
  if (!videos.length) return null;
  return (
    <div className="mb-10" data-testid={testid}>
      <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-900 mb-4 px-1">{title}</h2>
      <div className="flex gap-4 overflow-x-auto pb-3 bubz-scroll -mx-1 px-1">
        {videos.map((v) => (
          <div key={v.video_id} className="group relative shrink-0 w-56 sm:w-64">
            {isKid && (
              <button data-testid={`bookmark-${v.video_id}`} onClick={(e) => { e.stopPropagation(); onBookmark(v); }}
                className={`absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center shadow ${watchSet?.has(v.video_id) ? "bg-[#FFC02D] text-slate-900" : "bg-black/50 text-white"}`}>
                <Bookmark className={`h-4 w-4 ${watchSet?.has(v.video_id) ? "fill-slate-900" : ""}`} />
              </button>
            )}
            <button data-testid={`video-card-${v.video_id}`} onClick={() => onPlay(v)} className="w-full text-left">
              <motion.div whileHover={{ y: -6, scale: 1.03 }} className="relative rounded-2xl overflow-hidden shadow-lg aspect-video">
                <img src={v.thumbnail} alt={v.title} className="h-full w-full object-cover" />
                {v.finished && <span className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">✓ Watched</span>}
                {v.locked && (
                  <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white">
                    <Lock className="h-7 w-7" />
                    <span className="mt-1 rounded-full bg-[#FFC02D] text-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase">{v.tier}</span>
                  </div>
                )}
                {!v.locked && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                    <span className="h-12 w-12 rounded-full bg-[#FF5A79] text-white flex items-center justify-center shadow-lg">
                      <Play className="h-6 w-6 ml-0.5" />
                    </span>
                  </div>
                )}
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{v.duration}</span>
              </motion.div>
              <div className="mt-2 px-1">
                <div className="font-bold text-slate-800 text-sm truncate">{v.title}</div>
                <div className="text-xs text-slate-400">Ages {v.age_group}</div>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StreamingHub() {
  const { activeKid, isKid, refreshKid } = useAuth();
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [continueList, setContinueList] = useState([]);
  const [age, setAge] = useState("All");
  const [playing, setPlaying] = useState(null);
  const [reward, setReward] = useState(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [sub, setSub] = useState(undefined);
  const [upgradeVideo, setUpgradeVideo] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const watchSet = useMemo(() => new Set(watchlist.map((v) => v.video_id)), [watchlist]);

  const loadWatchlist = useCallback(() => {
    if (isKid) api.get(`/profiles/${activeKid.profile_id}/watchlist`).then(({ data }) => setWatchlist(data));
  }, [isKid, activeKid]);

  const toggleWatch = async (v) => {
    await api.post(`/profiles/${activeKid.profile_id}/watchlist`, { video_id: v.video_id });
    loadWatchlist();
  };

  useEffect(() => {
    if (!isKid) api.get("/subscription").then(({ data }) => setSub(data.subscription));
    loadWatchlist();
  }, [isKid, loadWatchlist]);

  const loadVideos = useCallback(() => {
    const url = isKid ? `/videos?profile_id=${activeKid.profile_id}` : "/videos";
    api.get(url).then(({ data }) => setVideos(data));
  }, [isKid, activeKid]);

  const loadContinue = useCallback(() => {
    if (isKid) api.get(`/profiles/${activeKid.profile_id}/continue`).then(({ data }) => setContinueList(data));
  }, [isKid, activeKid]);

  useEffect(() => {
    loadVideos();
    loadContinue();
    api.get("/videos/featured").then(({ data }) => setFeatured(data));
  }, [loadVideos, loadContinue]);

  const open = async (v) => {
    if (v.locked) {
      setUpgradeVideo(v);
      return;
    }
    setPlaying(v);
    if (isKid) {
      try { await api.post(`/profiles/${activeKid.profile_id}/watch`, { video_id: v.video_id }); loadContinue(); } catch (e) { console.error("watch progress update failed", e); }
    }
  };

  const onEnded = async (v) => {
    if (!isKid) return;
    try {
      const { data } = await api.post(`/profiles/${activeKid.profile_id}/finish`, { video_id: v.video_id });
      if (data.sticker) setReward(data.sticker);
      loadContinue();
      refreshKid();
    } catch (e) { console.error("finish video failed", e); }
  };

  const filtered = useMemo(
    () => (age === "All" || isKid ? videos : videos.filter((v) => v.age_group === age)),
    [videos, age, isKid]
  );

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <VideoModal video={playing} onClose={() => setPlaying(null)} onEnded={onEnded} />
      <StickerReward sticker={reward} onClose={() => setReward(null)} />
      <UpgradeSheet video={upgradeVideo} isKid={isKid} onClose={() => setUpgradeVideo(null)}
        onUpgrade={() => { setUpgradeVideo(null); setPlansOpen(true); }} />

      {featured && (
        <div className="relative h-[52vh] min-h-[380px] w-full overflow-hidden">
          <img src={featured.thumbnail} alt={featured.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFDF7] via-transparent to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto h-full flex flex-col justify-end px-6 pb-12">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl font-display font-bold text-white drop-shadow">Bubz <span className="text-[#FFC02D]">+</span></span>
              <span className="rounded-full bg-[#FFC02D] text-slate-900 px-3 py-1 text-xs font-bold">★ Featured Today</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-white max-w-xl drop-shadow">{featured.title}</h1>
            <p className="text-white/90 mt-3 max-w-lg">{featured.description}</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => open(featured)} data-testid="hero-play-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#FF5A79] text-white px-6 py-3 font-bold shadow-lg active:scale-95 transition-all">
                <Play className="h-5 w-5" /> Play Now
              </button>
              <button onClick={() => open(featured)}
                className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur text-white px-6 py-3 font-bold hover:bg-white/30 transition">
                <Info className="h-5 w-5" /> More Info
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 mt-8">
        {!isKid && sub !== undefined && (
          <div className="mb-6 rounded-3xl p-5 flex items-center gap-4 shadow-lg"
            style={{ background: sub ? "linear-gradient(135deg,#2EC4B6,#2AB7CA)" : "linear-gradient(135deg,#FF5A79,#9B5DE5)" }}
            data-testid="subscription-banner">
            <Crown className="h-8 w-8 text-white shrink-0" />
            <div className="flex-1 text-white">
              <div className="font-display font-bold text-lg">
                {sub ? `You're on the Bubz + ${sub.charAt(0).toUpperCase() + sub.slice(1)} plan` : "Unlock Bubz + streaming"}
              </div>
              <div className="text-white/90 text-sm">{sub ? "Thanks for subscribing! Manage your plan anytime." : "Choose a plan — or start free with the Demo tier."}</div>
            </div>
            <button onClick={() => setPlansOpen(true)} data-testid="hub-choose-plan-btn"
              className="rounded-full bg-white/90 text-slate-900 font-bold px-5 py-2.5 hover:bg-white transition active:scale-95">
              {sub ? "Change plan" : "See plans"}
            </button>
          </div>
        )}
        <SubscriptionPlans open={plansOpen} onClose={() => setPlansOpen(false)} current={sub}
          onChanged={() => api.get("/subscription").then(({ data }) => setSub(data.subscription))} />

        {isKid && (
          <p className="text-slate-500 mb-4">Picked for <span className="font-bold text-slate-700">{activeKid.name}</span> 🎈</p>
        )}

        {/* Age filters (parent preview only) */}
        {!isKid && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="age-filters">
            {AGE_FILTERS.map((a) => (
              <button key={a} data-testid={`age-filter-${a}`} onClick={() => setAge(a)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  age === a ? "bg-[#2AB7CA] text-white shadow" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"
                }`}>
                {a === "All" ? "All Ages" : a === "Parent" ? "Parent Ed" : `Ages ${a}`}
              </button>
            ))}
          </div>
        )}

        {/* Continue watching */}
        {isKid && continueList.filter((v) => !v.finished).length > 0 && (
          <Row title="Keep Watching" testid="continue-row" onPlay={open} isKid={isKid} watchSet={watchSet} onBookmark={toggleWatch}
            videos={continueList.filter((v) => !v.finished)} />
        )}

        {/* Watch later */}
        {isKid && watchlist.length > 0 && (
          <Row title="Watch Later ⭐" testid="watchlist-row" onPlay={open} isKid={isKid} watchSet={watchSet} onBookmark={toggleWatch}
            videos={watchlist} />
        )}

        {CATEGORIES.map((cat) => (
          <Row key={cat} title={cat} testid={`row-${cat}`} videos={filtered.filter((v) => v.category === cat)} onPlay={open}
            isKid={isKid} watchSet={watchSet} onBookmark={toggleWatch} />
        ))}

        {isKid && !filtered.length && (
          <div className="text-center py-20 text-slate-400">
            <History className="h-10 w-10 mx-auto mb-3 opacity-50" />
            No shows match the current safety settings. Ask a grown-up to adjust the Parent Zone.
          </div>
        )}
      </div>
    </div>
  );
}
